import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight, Star, Zap, Heart, Award, ShoppingCart, TrendingUp, Users, Shield } from 'lucide-react';
import { motion } from 'framer-motion';
import MagneticButton from '@/components/MagneticButton';
import AnimatedSection from '@/components/AnimatedSection';
import { Button } from '@/components/ui/button';
import Typewriter from 'typewriter-effect';
import { enhancedProducts } from '@/data/enhancedProducts';

const Hero = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(0);

  // Enhanced product showcase data featuring our collection highlights
  const showcaseProducts = [
    {
      name: "COOLING PERFORMANCE CREW",
      image: "/products/mens/cooling-performance-crew-main.jpg",
      features: "Moisture-wicking • UPF 50+ • Adaptive Fit • Cooling Tech",
      price: "$42",
      originalPrice: "$55",
      color: "lime-400",
      badge: "BESTSELLER",
      category: "Men's Performance"
    },
    {
      name: "WOMEN'S CROP TOP",
      image: "/products/womens/cooling-crop-top-main.jpg", 
      features: "Ultra-soft • Quick-dry • Supportive • Athletic Cut",
      price: "$38",
      originalPrice: "$48",
      color: "pink-400",
      badge: "NEW",
      category: "Women's Active"
    },
    {
      name: "SPORT-TEK HOODIE",
      image: "/products/mens/sport-tek-hoodie-main.jpg",
      features: "PosiCharge • Full-zip • Premium Embroidery • Comfort Fit",
      price: "$68",
      color: "purple-400",
      badge: "PREMIUM",
      category: "Outerwear"
    },
    {
      name: "PBX SPORT BAG",
      image: "/products/accessories/sport-bag-main.jpg",
      features: "Durable • Multi-compartment • Paddle Holder • Premium",
      price: "$55",
      color: "orange-400",
      badge: "BESTSELLER", 
      category: "Accessories"
    }
  ];

  // Auto-rotate products every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentProduct((prev) => (prev + 1) % showcaseProducts.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [showcaseProducts.length]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isMobile) {
        const x = (e.clientX / window.innerWidth) * 100;
        const y = (e.clientY / window.innerHeight) * 100;
        setMousePosition({ x, y });
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', checkMobile);
    };
  }, [isMobile]);

  // Calculate grid opacity based on scroll
  const gridOpacity = Math.max(0.2 - (scrollY / 600) * 0.15, 0.05);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target as HTMLElement;
            target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (heroRef.current) {
      observer.observe(heroRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const typingStrings = [
    "Performance Meets Purpose",
    "Built for Every Player",
    "Adaptive. Inclusive. Bold.",
    "Elevate Your Game",
    "18 Products. Endless Possibilities."
  ];

  // Enhanced stats from our product collection
  const collectionStats = {
    totalProducts: enhancedProducts.length,
    categories: 3,
    avgRating: 4.9,
    athletesServed: 2500,
    newArrivals: enhancedProducts.filter(p => p.badge === 'NEW').length,
    bestsellers: enhancedProducts.filter(p => p.badge === 'BESTSELLER').length
  };

  return (
    <section ref={heroRef} className={`relative min-h-screen flex items-center justify-center overflow-hidden bg-black ${isMobile ? 'pt-16 pb-2' : 'pt-24 pb-4'}`}>
      {/* Static Gradient Background */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-black via-zinc-900 to-black overflow-hidden"
      >
        {/* Animated Pickleball Court - Hidden on mobile */}
        {!isMobile && (
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div 
            className="relative w-[90vw] h-[45vw] md:w-[80vw] md:h-[40vw] max-w-[1200px] max-h-[600px]"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 0.2, scale: 1 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
          >
            {/* Court Outline with Glow Effect */}
            <motion.div 
              className="absolute inset-0 border-2 border-lime-400/30 rounded-sm"
              animate={{
                boxShadow: [
                  '0 0 20px rgba(199, 255, 0, 0)',
                  '0 0 30px rgba(199, 255, 0, 0.2)',
                  '0 0 20px rgba(199, 255, 0, 0)'
                ]
              }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            >
              {/* Animated Corner Accents */}
              {[0, 1, 2, 3].map((i) => (
                <motion.div
                  key={i}
                  className="absolute w-8 h-8 border-2 border-lime-400/40"
                  style={{
                    top: i < 2 ? -1 : 'auto',
                    bottom: i >= 2 ? -1 : 'auto',
                    left: i % 2 === 0 ? -1 : 'auto',
                    right: i % 2 === 1 ? -1 : 'auto',
                    borderTop: i >= 2 ? 'none' : undefined,
                    borderBottom: i < 2 ? 'none' : undefined,
                    borderLeft: i % 2 === 1 ? 'none' : undefined,
                    borderRight: i % 2 === 0 ? 'none' : undefined,
                  }}
                  animate={{
                    opacity: [0.3, 1, 0.3],
                    scale: [1, 1.2, 1]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: i * 0.5
                  }}
                />
              ))}
            </motion.div>

            {/* Center Line with Pulse Effect */}
            <motion.div 
              className="absolute left-1/2 top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-lime-400/40 to-transparent"
              animate={{
                opacity: [0.3, 0.6, 0.3],
                height: ['0%', '100%', '0%']
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
                times: [0, 0.5, 1]
              }}
            />

            {/* Non-Volley Zone (Kitchen) with Scanning Effect */}
            <motion.div 
              className="absolute left-[15%] right-[15%] top-[20%] bottom-[20%] border-2 border-lime-400/30"
              animate={{
                boxShadow: [
                  'inset 0 0 15px rgba(199, 255, 0, 0)',
                  'inset 0 0 15px rgba(199, 255, 0, 0.2)',
                  'inset 0 0 15px rgba(199, 255, 0, 0)'
                ]
              }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            />

            {/* Service Courts with Wave Effect */}
            <div className="absolute w-full h-full grid grid-cols-4 grid-rows-2">
              {[...Array(8)].map((_, i) => (
                <motion.div
                  key={i}
                  className="border border-lime-400/20"
                  animate={{
                    opacity: [0.1, 0.4, 0.1],
                    scale: [0.98, 1, 0.98]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: i * 0.15
                  }}
                />
              ))}
            </div>

            {/* Baseline Dots with Ripple Effect */}
            {[...Array(4)].map((_, i) => (
              <div key={i} className="absolute" style={{
                left: `${25 + i * 16.67}%`,
                top: '50%',
                transform: 'translate(-50%, -50%)'
              }}>
                <motion.div
                  className="absolute w-2 h-2 rounded-full bg-lime-400/40"
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.4, 0.8, 0.4]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: i * 0.3
                  }}
                />
                {/* Ripple Effect */}
                <motion.div
                  className="absolute w-2 h-2 rounded-full border border-lime-400/30"
                  style={{ top: 0, left: 0 }}
                  animate={{
                    scale: [1, 4],
                    opacity: [0.3, 0]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeOut",
                    delay: i * 0.3
                  }}
                />
              </div>
            ))}

            {/* Moving Ball Effect */}
            <motion.div
              className="absolute w-3 h-3 rounded-full bg-lime-400/50"
              animate={{
                pathOffset: [0, 1],
                opacity: [0.5, 0.8, 0.5]
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "linear"
              }}
              style={{
                offsetPath: "path('M 20% 50% C 40% 20%, 60% 80%, 80% 50%')"
              }}
            >
              {/* Ball Trail */}
              <motion.div
                className="absolute w-full h-full rounded-full bg-lime-400/20 blur-sm"
                animate={{ opacity: [0.3, 0] }}
                transition={{ duration: 0.5, repeat: Infinity }}
              />
            </motion.div>
          </motion.div>
        </div>
        )}

        {/* Static Mesh Gradient - Only on desktop */}
        {!isMobile && (
          <div 
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `
                repeating-linear-gradient(35deg, 
                  rgba(199, 255, 0, 0.1) 0px, 
                  transparent 2px, 
                  transparent 10px
                ),
                repeating-linear-gradient(-35deg, 
                  rgba(168, 85, 247, 0.1) 0px, 
                  transparent 2px, 
                  transparent 10px
                )
              `
            }}
          />
        )}
      </div>
      
      {/* Grid Overlay with Parallax - Only on desktop */}
      {!isMobile && (
      <div 
        className="absolute inset-0 transition-opacity duration-300 ease-out"
        style={{
          opacity: gridOpacity,
          backgroundImage: `url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGRlZnM+CjxwYXR0ZXJuIGlkPSJncmlkIiB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiPgo8cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjQzdGRjAwIiBzdHJva2Utd2lkdGg9IjAuNSIgb3BhY2l0eT0iMC4xIi8+CjwvcGF0dGVybj4KPC9kZWZzPgo8cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIiAvPgo8L3N2Zz4K')`,
          transform: `translateY(${scrollY * 0.2}px)`
        }}
      />
      )}
      
      {/* Noise Texture - Only on desktop */}
      {!isMobile && (
      <div 
        className="absolute inset-0 opacity-[0.015] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />
      )}
      
      {/* Content */}
      <div className="relative z-10 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
        {/* Mobile Layout - Vertical Stack */}
        {isMobile ? (
          <div className="flex flex-col justify-center min-h-screen space-y-2">
            
            {/* Top Half - Main Content (Punchline) */}
            <div className="flex-1 min-h-[45vh] flex items-center justify-center py-4">
              <div className="space-y-4 text-center max-w-sm mx-auto">
          {/* Main Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                  className="font-extrabold uppercase leading-[0.8] text-4xl sm:text-5xl tracking-tighter"
              style={{ 
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                fontWeight: 900,
                fontFeatureSettings: '"cv02", "cv03", "cv04", "cv11"'
              }}
            >
              <span 
                className="block"
                style={{
                  background: 'linear-gradient(135deg, #949598 0%, #c1c5c9 25%, #949598 50%, #e8ebef 75%, #949598 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  filter: 'drop-shadow(0 0 8px rgba(148, 149, 152, 0.3))'
                }}
              >
                Style&nbsp;That
              </span>
              <span className="block text-lime-400 drop-shadow-2xl">Serves</span>
              <span 
                className="block"
                style={{
                  background: 'linear-gradient(135deg, #949598 0%, #c1c5c9 25%, #949598 50%, #e8ebef 75%, #949598 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  filter: 'drop-shadow(0 0 8px rgba(148, 149, 152, 0.3))'
                }}
              >
                Everyone
              </span>
            </motion.h1>

            {/* Typing Effect */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                  className="h-[1.2em] flex items-center justify-center text-base"
            >
              <Typewriter
                options={{
                  strings: typingStrings,
                  autoStart: true,
                  loop: true,
                  delay: 50,
                  deleteSpeed: 30,
                  cursor: '|',
                  wrapperClassName: 'text-lime-400 font-bold',
                  cursorClassName: 'text-lime-400 animate-pulse',
                }}
              />
            </motion.div>

                {/* Subtitle */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  className="text-base text-gray-300 leading-relaxed font-light"
              style={{ 
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                fontWeight: 300,
                fontFeatureSettings: '"cv02", "cv03", "cv04", "cv11"'
              }}
            >
                  <p>Athletic apparel empowering players of all abilities</p>
            </motion.div>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="pt-0.5 md:pt-3"
                >
                  <Button 
                    size="lg"
                    className="bg-lime-400 text-black hover:bg-lime-300 font-bold text-base px-8 py-4 rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-lime-400/25 group w-full max-w-xs"
                    style={{ 
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      fontWeight: 700,
                      fontFeatureSettings: '"cv02", "cv03", "cv04", "cv11"'
                    }}
                  >
                    <ShoppingCart className="mr-2 w-5 h-5" />
                    SHOP COLLECTION
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </Button>
                </motion.div>
              </div>
            </div>

            {/* Middle Section - Enhanced Product Showcase */}
            <div className="flex-1 min-h-[35vh] flex items-center justify-center py-0 md:py-2">
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="bg-gradient-to-br from-black/30 to-black/10 rounded-xl p-4 border border-lime-400/30 backdrop-blur-sm w-full max-w-sm mx-auto relative overflow-hidden"
              >
                {/* Background glow effect */}
                <div className={`absolute inset-0 bg-gradient-to-br opacity-5 ${
                  currentProduct === 0 ? 'from-lime-400' : 
                  currentProduct === 1 ? 'from-pink-400' : 
                  currentProduct === 2 ? 'from-purple-400' : 'from-orange-400'
                } to-transparent pointer-events-none`}></div>

                {/* Product Badge */}
                <div className="absolute top-2 right-2 z-10">
                  <div className={`px-2 py-1 rounded-full text-xs font-bold ${
                    showcaseProducts[currentProduct].badge === 'NEW' ? 'bg-lime-400 text-black' :
                    showcaseProducts[currentProduct].badge === 'BESTSELLER' ? 'bg-orange-500 text-white' :
                    'bg-purple-500 text-white'
                  }`}>
                    {showcaseProducts[currentProduct].badge}
                  </div>
                </div>

                {/* Product Image */}
                <div className="relative overflow-hidden rounded-lg group mb-3">
                  <div className="aspect-[4/3] bg-gradient-to-br from-gray-900 to-black flex items-center justify-center">
                    <img 
                      src={showcaseProducts[currentProduct].image} 
                      alt={`${showcaseProducts[currentProduct].name} from PBX Pickle collection`} 
                      className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-all duration-700"
                      key={currentProduct}
                      onError={(e) => {
                        // Fallback to placeholder if image fails to load
                        (e.target as HTMLImageElement).src = `https://via.placeholder.com/400x300/1f2937/a3a3a3?text=${encodeURIComponent(showcaseProducts[currentProduct].name.split(' ')[0])}`;
                      }}
                    />
                  </div>
                  
                  {/* Category tag */}
                  <div className="absolute bottom-2 left-2 bg-black/70 rounded-md px-2 py-1">
                    <span className="text-white text-xs font-medium">
                      {showcaseProducts[currentProduct].category}
                    </span>
                  </div>
                  
                  {/* Navigation Dots */}
                  <div className="flex justify-center gap-2 mt-2">
                    {showcaseProducts.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentProduct(index)}
                        className={`w-2 h-2 rounded-full transition-all duration-300 hover:scale-125 ${
                          index === currentProduct 
                            ? (currentProduct === 0 ? 'bg-lime-400' : 
                               currentProduct === 1 ? 'bg-pink-400' : 
                               currentProduct === 2 ? 'bg-purple-400' : 'bg-orange-400')
                            : 'bg-white/30 hover:bg-white/50'
                        }`}
                      />
                    ))}
                  </div>
                </div>

                {/* Product Info */}
                <div className="text-center relative">
                  <h4 className={`font-black text-base mb-1 ${
                    currentProduct === 0 ? 'text-lime-400' : 
                    currentProduct === 1 ? 'text-pink-400' : 
                    currentProduct === 2 ? 'text-purple-400' : 'text-orange-400'
                  }`}>
                    {showcaseProducts[currentProduct].name}
                  </h4>
                  <p className="text-white/80 text-xs mb-2 leading-relaxed">
                    {showcaseProducts[currentProduct].features}
                  </p>
                  <div className="flex items-center justify-center gap-2 mb-3">
                    {showcaseProducts[currentProduct].originalPrice && (
                      <span className="text-gray-400 line-through text-sm">
                        {showcaseProducts[currentProduct].originalPrice}
                      </span>
                    )}
                    <span className={`font-black text-xl ${
                      currentProduct === 0 ? 'text-lime-400' : 
                      currentProduct === 1 ? 'text-pink-400' : 
                      currentProduct === 2 ? 'text-purple-400' : 'text-orange-400'
                    }`}>
                      {showcaseProducts[currentProduct].price}
                    </span>
                  </div>
                  
                  {/* Enhanced Collection Stats */}
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="text-lime-400 font-black text-sm">{collectionStats.athletesServed.toLocaleString()}</div>
                      <div className="text-white/60 text-xs">Athletes</div>
                    </div>
                    <div>
                      <div className="text-lime-400 font-black text-sm">{collectionStats.avgRating}★</div>
                      <div className="text-white/60 text-xs">Rating</div>
                    </div>
                    <div>
                      <div className="text-lime-400 font-black text-sm">{collectionStats.totalProducts}</div>
                      <div className="text-white/60 text-xs">Products</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Bottom Section - Enhanced Collection Highlights */}
            <div className="min-h-[20vh] flex items-center justify-center py-2">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="bg-gradient-to-br from-lime-400/10 to-purple-600/10 border border-lime-400/20 rounded-xl p-4 backdrop-blur-sm w-full max-w-sm mx-auto relative overflow-hidden"
              >
                {/* Background effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-lime-400/5 to-purple-400/5 pointer-events-none"></div>
                
                <div className="relative">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <TrendingUp className="w-4 h-4 text-lime-400" />
                    <h3 className="text-sm font-black text-lime-400 uppercase">Collection Highlights</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="text-center p-2 rounded-lg bg-black/20 border border-lime-400/10">
                      <div className="flex items-center justify-center mb-2">
                        <Zap className="w-4 h-4 mr-1 text-lime-400" />
                        <h4 className="font-bold text-lime-400 text-xs">ADAPTIVE</h4>
                      </div>
                      <p className="text-white/90 text-xs leading-tight">
                        Magnetic closures, inclusive design
                      </p>
                    </div>
                    
                    <div className="text-center p-2 rounded-lg bg-black/20 border border-purple-400/10">
                      <div className="flex items-center justify-center mb-2">
                        <Shield className="w-4 h-4 mr-1 text-purple-400" />
                        <h4 className="font-bold text-purple-400 text-xs">PREMIUM</h4>
                      </div>
                      <p className="text-white/90 text-xs leading-tight">
                        UPF 50+, moisture-wicking tech
                      </p>
                    </div>
                    
                    <div className="text-center p-2 rounded-lg bg-black/20 border border-orange-400/10">
                      <div className="flex items-center justify-center mb-2">
                        <Star className="w-4 h-4 mr-1 text-orange-400" />
                        <h4 className="font-bold text-orange-400 text-xs">VERSATILE</h4>
                      </div>
                      <p className="text-white/90 text-xs leading-tight">
                        Court to street styling
                      </p>
                    </div>
                    
                    <div className="text-center p-2 rounded-lg bg-black/20 border border-pink-400/10">
                      <div className="flex items-center justify-center mb-2">
                        <Users className="w-4 h-4 mr-1 text-pink-400" />
                        <h4 className="font-bold text-pink-400 text-xs">COMMUNITY</h4>
                      </div>
                      <p className="text-white/90 text-xs leading-tight">
                        Supporting inclusive sports
                      </p>
                    </div>
                  </div>

                  {/* Collection summary */}
                  <div className="mt-3 pt-3 border-t border-lime-400/20 text-center">
                    <div className="flex justify-center items-center gap-4 text-xs">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-lime-400 rounded-full"></div>
                        <span className="text-white/80">{collectionStats.newArrivals} New</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                        <span className="text-white/80">{collectionStats.bestsellers} Bestsellers</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                        <span className="text-white/80">{collectionStats.categories} Categories</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        ) : (
          /* Desktop Layout - Side by Side */
          <div className="space-y-8 md:space-y-12">
            
            {/* Top Row - Main Content Left, Product Showcase Right */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start">
              
              {/* Left Side - Main Content */}
              <div className="space-y-4 md:space-y-6 text-center lg:text-left order-2 lg:order-1">
                {/* Main Headline */}
                <motion.h1
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                  className="font-extrabold uppercase leading-[0.75] text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl tracking-tighter"
                  style={{ 
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    fontWeight: 900,
                    fontFeatureSettings: '"cv02", "cv03", "cv04", "cv11"'
                  }}
                >
                  <span 
                    className="block"
                    style={{
                      background: 'linear-gradient(135deg, #949598 0%, #c1c5c9 25%, #949598 50%, #e8ebef 75%, #949598 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                      filter: 'drop-shadow(0 0 8px rgba(148, 149, 152, 0.3))'
                    }}
                  >
                    Style&nbsp;That
                  </span>
                  <span className="block text-lime-400 drop-shadow-2xl">Serves</span>
                  <span 
                    className="block"
                    style={{
                      background: 'linear-gradient(135deg, #949598 0%, #c1c5c9 25%, #949598 50%, #e8ebef 75%, #949598 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                      filter: 'drop-shadow(0 0 8px rgba(148, 149, 152, 0.3))'
                    }}
                  >
                    Everyone
                  </span>
                </motion.h1>

                {/* Typing Effect */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                  className="h-[1.2em] flex items-center justify-center lg:justify-start text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl"
                >
                  <Typewriter
                    options={{
                      strings: typingStrings,
                      autoStart: true,
                      loop: true,
                      delay: 50,
                      deleteSpeed: 30,
                      cursor: '|',
                      wrapperClassName: 'text-lime-400 font-bold',
                      cursorClassName: 'text-lime-400 animate-pulse',
                    }}
                  />
                </motion.div>

                {/* Subtitle */}
                <motion.div 
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl text-gray-300 leading-relaxed font-light px-2 lg:px-0"
                  style={{ 
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    fontWeight: 300,
                    fontFeatureSettings: '"cv02", "cv03", "cv04", "cv11"'
                  }}
                >
                  <p>Athletic apparel empowering players of all abilities</p>
                </motion.div>

                {/* Single CTA Button */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="flex justify-center lg:justify-start pt-2"
                >
              <Button 
                size="lg"
                    className="bg-lime-400 text-black hover:bg-lime-300 font-bold text-base sm:text-lg px-8 sm:px-10 py-4 sm:py-5 rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-lime-400/25 group w-full sm:w-auto max-w-sm"
                style={{ 
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  fontWeight: 700,
                  fontFeatureSettings: '"cv02", "cv03", "cv04", "cv11"'
                }}
              >
                    <ShoppingCart className="mr-2 w-5 h-5" />
                    SHOP COLLECTION
                    <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
                </motion.div>
              </div>

              {/* Right Side - Product Showcase */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="bg-gradient-to-br from-black/20 to-black/10 rounded-xl p-6 sm:p-8 border border-lime-400/30 backdrop-blur-sm order-1 lg:order-2 mt-8 lg:mt-16"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-center">
                  {/* Product Image */}
                  <div className="relative overflow-hidden rounded-lg group">
                    <div className="aspect-[4/3] bg-gradient-to-br from-gray-900 to-black flex items-center justify-center">
                      <img 
                        src={showcaseProducts[currentProduct].image} 
                        alt={`${showcaseProducts[currentProduct].name} apparel`} 
                        className="w-full h-full object-contain opacity-90 group-hover:scale-105 transition-all duration-700"
                        key={currentProduct}
                      />
                    </div>
                    
                    {/* Navigation Dots */}
                    <div className="flex justify-center gap-2 mt-4">
                      {showcaseProducts.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentProduct(index)}
                          className={`w-3 h-3 rounded-full transition-colors duration-300 ${
                            index === currentProduct 
                              ? (currentProduct === 0 ? 'bg-lime-400' : 
                                 currentProduct === 1 ? 'bg-purple-400' : 'bg-orange-400')
                              : 'bg-white/30'
                          }`}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Product Info */}
                  <div className="text-center sm:text-left">
                    <h4 className={`font-black text-lg sm:text-xl lg:text-2xl mb-3 ${
                      currentProduct === 0 ? 'text-lime-400' : 
                      currentProduct === 1 ? 'text-purple-400' : 'text-orange-400'
                    }`}>
                      {showcaseProducts[currentProduct].name}
                    </h4>
                    <p className="text-white text-sm sm:text-base lg:text-lg mb-4">{showcaseProducts[currentProduct].features}</p>
                    <div className="flex items-center justify-center sm:justify-start gap-3 mb-6">
                      {showcaseProducts[currentProduct].originalPrice && (
                        <span className="text-gray-400 line-through text-base sm:text-lg">
                          {showcaseProducts[currentProduct].originalPrice}
                        </span>
                      )}
                      <span className={`font-black text-2xl sm:text-3xl lg:text-4xl ${
                        currentProduct === 0 ? 'text-lime-400' : 
                        currentProduct === 1 ? 'text-purple-400' : 'text-orange-400'
                      }`}>
                        {showcaseProducts[currentProduct].price}
                      </span>
                    </div>
                    
                    {/* Social Proof */}
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-lime-400 font-black text-base sm:text-lg lg:text-xl">847</div>
                        <div className="text-white/60 text-sm">Athletes</div>
                      </div>
                      <div>
                        <div className="text-lime-400 font-black text-base sm:text-lg lg:text-xl">4.9★</div>
                        <div className="text-white/60 text-sm">Rating</div>
                      </div>
                      <div>
                        <div className="text-lime-400 font-black text-base sm:text-lg lg:text-xl">10%</div>
                        <div className="text-white/60 text-sm">To Sports</div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Bottom Row - Revolutionary Features - Only on desktop */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="bg-gradient-to-br from-lime-400/10 to-purple-600/10 border border-lime-400/20 rounded-xl p-3 sm:p-4 md:p-5 backdrop-blur-sm"
            >
              <h3 className="text-sm sm:text-base md:text-lg font-black text-lime-400 mb-3 sm:mb-4 uppercase text-center">Revolutionary Features</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <div className="text-center">
                  <div className="flex items-center justify-center mb-2">
                    <Zap className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-lime-400" />
                    <h4 className="font-bold text-lime-400 text-xs sm:text-sm">ADAPTIVE-FIRST</h4>
                  </div>
                  <p className="text-white/90 text-xs sm:text-sm leading-tight px-2">
                    Magnetic closures, wheelchair cuts
                  </p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center mb-2">
                    <Award className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-lime-400" />
                    <h4 className="font-bold text-lime-400 text-xs sm:text-sm">PERFORMANCE</h4>
                  </div>
                  <p className="text-white/90 text-xs sm:text-sm leading-tight px-2">
                    Moisture-wicking, UV protection
                  </p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center mb-2">
                    <Star className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-lime-400" />
                    <h4 className="font-bold text-lime-400 text-xs sm:text-sm">STREETWEAR</h4>
                  </div>
                  <p className="text-white/90 text-xs sm:text-sm leading-tight px-2">
                    Court to street seamless
                  </p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center mb-2">
                    <Heart className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-lime-400" />
                    <h4 className="font-bold text-lime-400 text-xs sm:text-sm">PURPOSE-DRIVEN</h4>
                  </div>
                  <p className="text-white/90 text-xs sm:text-sm leading-tight px-2">
                    10% funds sports programs
                  </p>
                </div>
            </div>
          </motion.div>
        </div>
        )}
      </div>
    </section>
  );
};

export default Hero;
